import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useAnalytics } from '../hooks/useAnalytics';
import { useTranslations } from '../hooks/useTranslations';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Link2, Mail, Lock, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';

export const LoginPage = () => {
  const { login, error: authError } = useAuth();
  const { language } = useLanguage();
  const t = useTranslations();
  const { trackPageView, trackLogin } = useAnalytics();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Page-specific texts that extend the base translations
  const pageTexts = {
    es: {
      subtitle: 'Accede a tu cuenta para ver el catálogo',
    },
    en: {
      subtitle: 'Access your account to view the catalog',
    },
    de: {
      subtitle: 'Melden Sie sich an, um den Katalog zu sehen',
    },
    fr: {
      subtitle: 'Connectez-vous pour voir le catalogue',
    },
    it: {
      subtitle: 'Accedi al tuo account per vedere il catalogo',
    },
  };

  const texts = pageTexts[language] || pageTexts.es;

  // Track page view on mount
  useEffect(() => {
    trackPageView('/login');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      trackLogin(null, email);
      navigate('/');
    } else {
      setError(result.error || t.auth.invalidCredentials);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-subtle-gradient p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Link2 className="h-6 w-6" />
          </div>
          <span className="font-display text-2xl font-bold text-foreground">
            Key<span className="text-primary">Publi</span> <span className="text-sm font-normal text-muted-foreground">Link Building</span>
          </span>
        </Link>

        <Card className="shadow-xl border-border/60">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-2xl font-display">{t.auth.login}</CardTitle>
            <CardDescription>{texts.subtitle}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {(error || authError) && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error || authError}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">{t.auth.email}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t.auth.password}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t.auth.login}...
                  </>
                ) : (
                  t.auth.login
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm space-y-2">
              <div>
                <Link to="/forgot-password" className="text-primary hover:underline font-medium">
                  {t.auth.forgotPassword}
                </Link>
              </div>
              <div>
                <span className="text-muted-foreground">{t.auth.noAccount} </span>
                <Link to="/register" className="text-primary hover:underline font-medium">
                  {t.auth.register}
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
